import { useEffect, useMemo, useRef } from 'react';
import type { Mutate, StoreApi } from 'zustand';

type NavigateFn<T> = (args: { search: T; replace?: boolean }) => void;

function isDefaultSearch<TSearch extends object>(
    searchQuery: TSearch,
    defaultSearchQuery: TSearch | undefined,
) {
    return !Object.entries(defaultSearchQuery ?? {}).some(
        ([key, value]) =>
            key in searchQuery && searchQuery[key as keyof TSearch] !== value,
    );
}

function jsonEqual(a: unknown, b: unknown) {
    return JSON.stringify(a) === JSON.stringify(b);
}

/**
 * Used to synchronize a zustand store data with URL search params.
 * On mount (after hydration if present), the data is loaded from the store and pushed to the URL if different.
 * After that, any URL changes are saved to the store.
 */
export function useSyncStoreAndSearch<
    TStore extends object,
    TSearch extends object = TStore,
>({
    store,
    searchQuery,
    defaultSearchQuery,
    navigate,
    loadFromStore,
    saveToStore,
}: {
    store: Mutate<StoreApi<TStore>, [['zustand/persist', unknown]]>;
    searchQuery: TSearch;
    defaultSearchQuery?: TSearch;
    navigate: NavigateFn<TSearch>;
    loadFromStore?: (state: TStore) => TSearch;
    saveToStore?: (state: TStore, next: TSearch) => TStore;
}) {
    const didInit = useRef(false);

    const loadSearchFromStore = useMemo(
        () => loadFromStore ?? ((state: unknown) => state as TSearch),
        [loadFromStore],
    );

    const saveSearchToStore = useMemo(
        () =>
            saveToStore ??
            ((_: TStore, next: TSearch) => next as unknown as TStore),
        [saveToStore],
    );

    const initValuesRef = useRef({
        store,
        searchQuery,
        defaultSearchQuery,
        navigate,
        loadSearchFromStore,
    });

    useEffect(() => {
        initValuesRef.current = {
            store,
            searchQuery,
            defaultSearchQuery,
            navigate,
            loadSearchFromStore,
        };
    }, [store, searchQuery, defaultSearchQuery, navigate, loadSearchFromStore]);

    // 1) Wait for persist hydration, if present, then push store -> URL once.
    // Only push store -> URL if the initial query is still the default one.
    useEffect(() => {
        if (didInit.current) return;

        const runInit = () => {
            if (didInit.current) return;

            const {
                store,
                searchQuery,
                defaultSearchQuery,
                navigate,
                loadSearchFromStore,
            } = initValuesRef.current;

            const fromStore = loadSearchFromStore(store.getState());

            if (isDefaultSearch(searchQuery, defaultSearchQuery)) {
                navigate({ search: fromStore, replace: true });
            }

            didInit.current = true;
        };

        const ready = store.persist?.hasHydrated?.() ?? true;

        // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler
        if (!ready) {
            // eslint-disable-next-line react-you-might-not-need-an-effect/no-pass-data-to-parent
            const unsub = store.persist?.onFinishHydration?.(() => {
                queueMicrotask(runInit);
            });

            return () => unsub?.();
        }

        runInit();
    }, [store]);

    // 2) After init: URL -> store.
    useEffect(() => {
        if (!didInit.current) return;

        const current = loadSearchFromStore(store.getState());

        if (!jsonEqual(current, searchQuery)) {
            // eslint-disable-next-line react-you-might-not-need-an-effect/no-pass-data-to-parent
            store.setState(
                (prev) => saveSearchToStore(prev, searchQuery) as TStore,
                false,
            );
        }
    }, [searchQuery, store, loadSearchFromStore, saveSearchToStore]);
}
